import { T } from '@/i18n/LocaleProvider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button } from '@/components';
import { MONO_FONT } from '@/theme';

export default function PageHero({
  tag,
  meta = [],
  title,
  subtitle,
  actions = [],
  strip = [],
}) {
  return (
    <Box
      component="header"
      data-nav-theme="light"
      sx={{
        bgcolor: 'grey.50',
        borderBottom: '1px solid #e2e5e3',
        pt: '96px',
        pb: { xs: '48px', md: '64px' },
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '800px',
          height: '800px',
          right: '-160px',
          top: '-200px',
          background:
            'radial-gradient(closest-side, rgba(0,159,139,0.07), transparent 70%)',
          pointerEvents: 'none',
          zIndex: 0,
        },
      }}
    >
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          px: { xs: '20px', sm: '32px' },
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Meta bar */}
        <Box
          sx={{
            display: 'flex',
            gap: { xs: '16px', md: '24px' },
            flexWrap: 'wrap',
            fontFamily: MONO_FONT,
            fontSize: '11px',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'text.muted',
            pb: '28px',
            borderBottom: '1px solid #e2e5e3',
            mb: { xs: '40px', md: '56px' },
            alignItems: 'center',
          }}
        >
          {/* Eyebrow tag — line-accent style */}
          {tag && (
            <Box
              component="span"
              sx={{
                fontFamily: MONO_FONT,
                fontSize: '11px',
                fontWeight: 500,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'text.accent',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                '&::before': {
                  content: '""',
                  width: '24px',
                  height: '1px',
                  background: 'currentColor',
                  flexShrink: 0,
                },
              }}
            >
              <T>{tag}</T>
            </Box>
          )}
          {meta.map((item, i) => (
            <Box
              key={i}
              component="span"
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                ...(item.dot ? { color: 'grey.300' } : {}),
                ...(item.sx || {}),
              }}
            >
              <T>{item.text}</T>
            </Box>
          ))}
        </Box>

        {/* H1 */}
        <Typography
          variant="h1"
          component="h1"
          sx={{
            fontSize: 'clamp(40px, 5vw, 72px)',
            letterSpacing: '-0.025em',
            lineHeight: 1.05,
            color: 'text.primary',
            maxWidth: '20ch',
            pt: 4,
          }}
        >
          <T>{title}</T>
        </Typography>

        {/* Subtitle */}
        {subtitle && (
          <Typography
            sx={{
              mt: 4,
              fontSize: 'clamp(16px, 1vw, 18px)',
              lineHeight: 1.5,
              color: 'text.muted',
              maxWidth: '66ch',
              fontWeight: 400,
              letterSpacing: '-0.005em',
            }}
          >
            <T>{subtitle}</T>
          </Typography>
        )}

        {/* Actions */}
        {actions.length > 0 && (
          <Box
            sx={{
              mt: { xs: 3.5, md: 4.5 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            {actions.map((action, i) => (
              <Button
                key={i}
                href={action.href}
                variant={i === 0 ? 'contained' : 'ghost'}
                arrow={i === 0}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
                {...(action.external && { external: true })}
                {...(action.target && { target: action.target })}
                {...(action.rel && { rel: action.rel })}
                {...(action.gtmEvent && { 'data-gtm-event': action.gtmEvent })}
                {...(action.gtmSource && {
                  'data-gtm-source': action.gtmSource,
                })}
              >
                <T>{action.label}</T>
              </Button>
            ))}
          </Box>
        )}

        {/* Strip */}
        {strip.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                md: `repeat(${strip.length}, 1fr)`,
              },
              borderTop: '1px solid #e2e5e3',
              mt: { xs: 5, md: 13 },
              mb: 4,
            }}
          >
            {strip.map((item, i) => (
              <Box
                key={i}
                sx={{
                  pt: { xs: '16px', md: '24px' },
                  pb: { xs: '16px', md: '12px' },
                  pr: {
                    xs: i % 2 === 0 ? '16px' : 0,
                    md: i < strip.length - 1 ? '24px' : 0,
                  },
                  pl: {
                    xs: i % 2 === 1 ? '16px' : 0,
                    md: i > 0 ? '24px' : 0,
                  },
                  borderRight: {
                    xs: i % 2 === 0 ? '1px solid #e2e5e3' : 'none',
                    md: i < strip.length - 1 ? '1px solid #e2e5e3' : 'none',
                  },
                  borderBottom: {
                    xs: i < 2 ? '1px solid #e2e5e3' : 'none',
                    md: 'none',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: MONO_FONT,
                    fontSize: '10.5px',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'text.muted',
                    mb: 1.5,
                  }}
                >
                  <T>{item.label}</T>
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '23px', md: '28px' },
                    fontWeight: 400,
                    letterSpacing: '-0.02em',
                    color: 'text.primary',
                    lineHeight: 1.05,
                    mb: 1.5,
                  }}
                >
                  <T>{item.value}</T>
                </Typography>
                <Typography
                  sx={{
                    fontFamily: MONO_FONT,
                    fontSize: '11px',
                    color: 'text.muted',
                    mt: 1,
                  }}
                >
                  <T>{item.sub}</T>
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
